/**
 * Registration in the checkout: entry → delivery target → billing address.
 *
 * Markup contract: the component sits on the registration form and owns the three frames
 * (#entryFrame, #selectionFrame, #billingFrame), the delivery target radios
 * (name="regDelOption") and their panels (#regDelHomeBody, #regDelFilialBody,
 * #regDelPickupBody).
 *
 * Only the chosen delivery target may be sent, so the panels of the other two are
 * disabled — that also keeps them out of the validation. Entries and step survive a
 * reload in localStorage, keyed by target; registrationState.js holds the keys and the
 * later steps clear them.
 */
import { clearRegistrationState } from './registrationState.js';

const TARGETS = {
    home: { panel: 'regDelHomeBody', storage: 'form.regHomeForm' },
    filial: { panel: 'regDelFilialBody', storage: 'form.regFilialeForm' },
    pickup: { panel: 'regDelPickupBody', storage: 'form.regPickupForm' },
};

// the panel ids the old widget wrote to localStorage, kept so a half-finished checkout survives
const PANEL_TO_TARGET = Object.fromEntries(
    Object.entries(TARGETS).map(([target, { panel }]) => [panel, target]),
);

function read(key) {
    try {
        return JSON.parse(localStorage.getItem(key)) ?? {};
    } catch (error) {
        return {};
    }
}

document.addEventListener('alpine:init', () => {
    Alpine.data('checkoutRegistration', () => ({
        frame: 'entry',
        target: '',
        billingSeparate: false,

        init() {
            this.form = this.$el.closest('form');
            this.frame = localStorage.getItem('registrationFrame') ?? 'entry';
            this.target = PANEL_TO_TARGET[localStorage.getItem('accOpen')] ?? '';
            this.billingSeparate = localStorage.getItem('showBillingForm') === 'true';

            if (this.target) {
                this.form.querySelector(`input[name="regDelOption"][aria-controls="${TARGETS[this.target].panel}"]`).checked = true;
                this.restore();
            } else {
                this.frame = 'entry';
            }

            this.form.addEventListener('change', () => this.save());

            Alpine.effect(() => this.apply());
        },

        panelOf(target) {
            return document.getElementById(TARGETS[target].panel);
        },

        apply() {
            document.getElementById('entryFrame').style.display = this.frame === 'entry' ? 'block' : 'none';
            document.getElementById('selectionFrame').style.display = this.frame === 'selection' ? 'block' : 'none';
            document.getElementById('billingFrame').hidden = this.frame !== 'billing';

            // a disabled fieldset takes its fields out of the submit and out of the validation
            // without touching their own disabled flag, which countryState needs
            Object.keys(TARGETS).forEach((target) => {
                const panel = this.panelOf(target);
                if (panel) {
                    panel.disabled = target !== this.target;
                }
            });

            // with a store or a packstation the billing address is a step of its own
            const ownStep = this.target !== '' && this.target !== 'home';
            const billingHost = document.getElementById(
                ownStep || this.frame === 'billing' ? 'billingFormInBillingFrame' : 'billingFormInSelectionFrame',
            );
            const billingForm = document.getElementById('billingForm');
            if (billingForm && billingHost && billingForm.parentNode !== billingHost) {
                billingHost.append(billingForm);
            }

            const selectionHost = document.getElementById('billingFormInSelectionFrame');
            const billingHosts = [selectionHost, document.getElementById('billingFormInBillingFrame')];
            selectionHost.style.display = !ownStep && this.billingSeparate ? 'block' : 'none';
            const billingInPlay = this.target !== '' && (ownStep || this.billingSeparate);
            billingHosts.forEach((host) => { host.disabled = !billingInPlay; });
            ['regBillMenu', 'deliveryAddressSummaryBox', 'billingAddressPrompt'].forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    element.style.display = ownStep ? 'block' : 'none';
                }
            });

            // the email and password of the billing form belong to the billing step;
            // with a home delivery they sit in the delivery panel instead
            const additional = document.getElementById('billAdditionalFields');
            if (additional) {
                const wanted = ownStep;
                additional.style.display = wanted ? 'block' : 'none';
                additional.disabled = !wanted;
            }
        },

        chooseTarget(target) {
            this.target = target;
            localStorage.setItem('accOpen', TARGETS[target].panel);
            this.restore();
            document.getElementById(`${TARGETS[target].panel.replace('Body', 'Title')}`)
                ?.scrollIntoView({ behavior: 'smooth' });
        },

        goTo(frame) {
            this.frame = frame;
            localStorage.setItem('registrationFrame', frame);
            if (frame === 'billing') {
                document.getElementById('billingFrame').scrollIntoView({ behavior: 'smooth' });
            }
        },

        toggleBillingSeparate(checkbox) {
            this.billingSeparate = !checkbox.checked;
            localStorage.setItem('showBillingForm', String(this.billingSeparate));
        },

        /** Guest or account: the hidden option field is what the server reads. */
        setAccountWanted(checkbox) {
            this.form.querySelector('input[name="option"]').value = checkbox.checked ? '3' : '1';
            this.form.querySelectorAll('.passwordField').forEach((field) => {
                field.hidden = !checkbox.checked;
                field.querySelectorAll('input').forEach((input) => { input.disabled = !checkbox.checked; });
            });
        },

        /** Leaves the delivery step with the store or the packstation as the target. */
        confirmDeliveryTarget(summary) {
            localStorage.setItem('displayRegBillUserEmail', 'true');
            Object.entries(summary).forEach(([id, text]) => {
                const element = document.getElementById(id);
                if (element) {
                    element.textContent = text;
                }
            });
            this.goTo('billing');
        },

        save() {
            if (!this.target) {
                return;
            }

            const values = {};
            this.form.querySelectorAll('input, select, textarea').forEach((field) => {
                // a field in a disabled fieldset is out of play, but its own disabled stays false
                if (!field.name || field.type === 'password' || field.type === 'hidden'
                    || field.disabled || field.closest('fieldset:disabled')) {
                    return;
                }
                if (field.type === 'radio') {
                    if (field.checked) {
                        values[field.name] = field.value;
                    }
                    return;
                }
                values[field.name] = field.type === 'checkbox' ? field.checked : field.value;
            });

            localStorage.setItem(TARGETS[this.target].storage, JSON.stringify(values));
        },

        restore() {
            const values = read(TARGETS[this.target].storage);

            Object.entries(values).forEach(([name, value]) => {
                this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((field) => {
                    if (field.type === 'radio') {
                        field.checked = field.value === value;
                    } else if (field.type === 'checkbox') {
                        field.checked = value;
                    } else {
                        field.value = value;
                    }
                });
            });

            const guest = this.form.querySelector('.guestCheckbox');
            if (guest) {
                this.setAccountWanted(guest);
            }
        },

        clearState: clearRegistrationState,
    }));
});
